import {
  AlertIcon,
  CaretDownIcon,
  CaretUpIcon,
} from '@tovala/component-library'
import { clsx } from 'clsx'
import {
  NutritionalInfoItem,
  RoutineMetadataObject,
  useIntentRoutine,
  useOvens,
} from '@tovala/browser-apis-combinedapi'
import { ReactNode, RefObject } from 'react'

import { getFormattedCookTime, getTotalCookTimeInSeconds } from 'utils/routines'

import { useUser } from 'contexts/user'
import Collapsible, {
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/common/Collapsible'
import { getFormattedExpirationDate, getMealBarcode } from 'utils/meals'
import { events } from 'analytics/events'
import { track } from 'utils/analytics'

export type SectionID =
  | 'allergens'
  | 'in_the_box'
  | 'ingredients'
  | 'nutrition'
  | 'prep_steps'
  | 'reviews'

export const Allergen = ({ allergen }: { allergen: string }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="h-6 w-6">
        <AlertIcon />
      </div>
      <span className="text-center text-k/14_120 text-grey-9">{allergen}</span>
    </div>
  )
}

export const Allergens = ({ allergens }: { allergens: string[] }) => {
  return (
    <div>
      {allergens.length > 0 && (
        <div className="flex space-x-4">
          {allergens.map((allergen) => {
            return <Allergen key={allergen} allergen={allergen} />
          })}
        </div>
      )}
      <p className="mt-4 text-k/14_120 text-grey-9">
        Produced and packed in a facility that handles milk, wheat, eggs, fish,
        shellfish, tree nuts, peanuts, soy, and gluten.
      </p>
    </div>
  )
}

export const ChefImage = ({
  chefImage,
  createdBy,
}: {
  chefImage: string
  createdBy: string
}) => {
  return (
    <div className="h-20 w-20 overflow-hidden rounded-full">
      <img alt="" className="w-20" src={chefImage} />
      <div className="absolute bottom-1 h-20 w-20 text-h/14_120 font-semibold uppercase tracking-[4px]">
        <svg className="block overflow-visible" viewBox="0 0 100 100">
          <path d="M0,40 c0,80 100,80 100,0" fill="none" id="curve" />
          <text
            style={{
              textAnchor: 'middle',
              fill: '#FFF',
              textShadow: '0px 0px 2px #000',
            }}
          >
            <textPath startOffset="50%" xlinkHref="#curve">
              Chef {createdBy}
            </textPath>
          </text>
        </svg>
      </div>
    </div>
  )
}

export const ComponentImages = ({
  images,
}: {
  images: { url: string; caption: string }[]
}) => {
  return (
    <div className="space-y-2 text-k/14_120">
      {images.map(({ caption, url }) => {
        return (
          <div key={url} className="flex items-center">
            <div className="w-20 text-center">
              <img alt="" className="inline" src={url} />
            </div>
            <div className="ml-2">{caption}</div>
          </div>
        )
      })}
    </div>
  )
}

export const CookTime = (
  params:
    | {
        mealID: number
        routineMetadataObjects: RoutineMetadataObject[] | null
      }
    | { barcode: string },
) => {
  const { user } = useUser()

  const userID = user.id

  const { data: getOvensResponse } = useOvens({ userID })

  // For the purposes of cook time, we're just taking the user's first oven. For the scope
  // of this work, that's what we want to do. If we needed to show multiple ovens, we'd
  // probably update the UI to allow a user to select one of their ovens.
  const ovenID = getOvensResponse?.[0]?.id

  const barcodeIdentifier =
    'routineMetadataObjects' in params
      ? params.routineMetadataObjects?.find(
          (routine) => routine.isDefaultRoutine,
        )?.barcodeIdentifier
      : undefined

  const barcode =
    'barcode' in params
      ? params.barcode
      : barcodeIdentifier
        ? getMealBarcode({ mealID: params.mealID, barcodeIdentifier })
        : ''

  const { data: getIntentRoutineResponse } = useIntentRoutine({
    ovenID,
    routineBarcode: barcode,
    userID,
  })

  const routineSteps = getIntentRoutineResponse?.routine.routine

  if (!routineSteps) {
    return null
  }

  const formattedCookTime = getFormattedCookTime({
    cookTimeInSeconds: getTotalCookTimeInSeconds({ routineSteps }),
  })

  return (
    <span className="shrink-0 py-1 text-k/20_110">{formattedCookTime} min</span>
  )
}

export function trackDetailsSectionOpened(sectionID: SectionID) {
  track(events.OPENS_MEAL_DETAIL_DISCLOSURE_TAB, {
    meal_detail_section_id: sectionID,
  })
}

export const DetailsCollapsible = ({
  children,
  defaultOpen,
  onCollapsibleClick,
  sectionID,
  title,
  triggerRef,
}: {
  children: ReactNode
  defaultOpen?: boolean
  onCollapsibleClick(sectionID: SectionID): void
  sectionID: SectionID
  title: string
  triggerRef?: RefObject<HTMLButtonElement>
}) => {
  return (
    <Collapsible defaultOpen={defaultOpen}>
      {({ open }) => {
        return (
          <>
            <CollapsibleTrigger ref={triggerRef} className="w-full py-4">
              <div
                className="flex items-center justify-between"
                onClick={() => {
                  if (!open) {
                    onCollapsibleClick(sectionID)
                  }
                }}
              >
                <h3 className="text-k/20_125 md:text-k/18_120">{title}</h3>
                {open ? (
                  <div className="h-6 w-6">
                    <CaretUpIcon />
                  </div>
                ) : (
                  <div className="h-6 w-6">
                    <CaretDownIcon />
                  </div>
                )}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent open={open}>
              <div className="pb-12">{children}</div>
            </CollapsibleContent>
          </>
        )
      }}
    </Collapsible>
  )
}

export const ExpirationDate = ({
  expirationDate,
}: {
  expirationDate: string
}) => {
  // If the expiration date is not set, don't show it.
  if (expirationDate === '0001-01-01T00:00:00Z') {
    return null
  }

  const formattedExpirationDate = getFormattedExpirationDate(expirationDate)
  if (!formattedExpirationDate) {
    return null
  }
  return (
    <div className="mb-8 inline-block bg-white px-3 py-1 text-h/11_120 font-semibold uppercase md:mb-6">
      <span className="mr-1 inline-block h-[10px] w-[10px] rounded-full bg-orange-1" />
      Enjoy before {formattedExpirationDate}
    </div>
  )
}

export const Ingredients = ({ ingredients }: { ingredients: string }) => {
  return (
    <div className="whitespace-pre-wrap text-k/14_120 capitalize text-grey-9">
      {ingredients}
    </div>
  )
}

export const MainImage = ({
  chefImage,
  imageTag,
  imageURL,
  stepper,
}: {
  chefImage?: ReactNode
  imageTag?: ReactNode
  imageURL?: string
  stepper?: ReactNode
}) => {
  return (
    <div
      className={clsx('relative h-[334px] overflow-hidden md:h-[275px]', {
        'bg-grey-4': !imageURL,
      })}
    >
      {imageURL && (
        <img alt="" className="h-full w-full object-cover" src={imageURL} />
      )}

      {imageTag && <div className="absolute left-4 top-4">{imageTag}</div>}

      {chefImage && <div className="absolute right-4 top-4">{chefImage}</div>}

      {stepper && <div className="absolute bottom-4 right-4">{stepper}</div>}
    </div>
  )
}

export const Nutrition = ({
  nutritionalInfo,
}: {
  nutritionalInfo: Partial<NutritionalInfoItem>[]
}) => {
  const hasGarnishes = nutritionalInfo.some(({ key }) => key === 'garnish')

  return (
    <div>
      <div className="divide-y divide-grey-3 border-b border-grey-3">
        {nutritionalInfo
          .filter(({ key }) => key === 'golden' || key === 'sub')
          .map(({ name, value, unit }) => {
            return (
              <NutritionRow key={name} name={name} unit={unit} value={value} />
            )
          })}
      </div>

      {hasGarnishes && (
        <div className="mt-4">
          <h3 className="mb-4 text-k/20_125 md:text-k/18_120">
            Optional Garnishes
          </h3>
          <div className="divide-y divide-grey-3 border-b border-grey-3">
            {nutritionalInfo
              .filter(({ key }) => key === 'garnish')
              .map(({ name, value, unit }) => {
                return (
                  <NutritionRow
                    key={name}
                    name={name}
                    unit={unit && ` ${unit.toLowerCase()}`}
                    value={`+${value}`}
                  />
                )
              })}
          </div>
        </div>
      )}
    </div>
  )
}

const NutritionRow = ({
  name,
  unit,
  value,
}: {
  name: NutritionalInfoItem['name'] | undefined
  unit: NutritionalInfoItem['unit'] | undefined
  value: NutritionalInfoItem['value'] | undefined
}) => {
  return (
    <div
      className={clsx(
        'flex justify-between py-1 text-k/14_120 tracking-normal text-grey-9',
        name === 'Calories' && 'border-b-2 border-grey-8',
      )}
    >
      <span>{name}</span>
      <span>
        {value}
        {unit}
      </span>
    </div>
  )
}

export const PreparationStep = ({ step }: { step: string }) => {
  return (
    <div>
      <span>{step}</span>
    </div>
  )
}

export const PreparationSteps = ({ steps }: { steps: string[] }) => {
  return (
    <ol className="space-y-4">
      {steps.map((step, index) => {
        return (
          <li key={step} className="flex">
            <span className="mr-4 w-10 shrink-0 text-k/32_105">
              {index + 1}.
            </span>
            <PreparationStep key={step} step={step} />
          </li>
        )
      })}
    </ol>
  )
}
